import { useUserPermissions } from '../../utils/hooks/useUserPermissions';

/**
 * PermissionGate component - Conditionally renders children based on user permissions
 * 
 * Usage examples:
 * <PermissionGate permission="edit_qr">
 *   <EditButton />
 * </PermissionGate>
 * 
 * <PermissionGate permission="manage_users" fallback={<div>Access Denied</div>}>
 *   <UserManagement />
 * </PermissionGate>
 * 
 * <PermissionGate requirePrimary>
 *   <TransferOwnership />
 * </PermissionGate>
 * 
 * @param {string} permission - Permission to check (analytics, edit_qr, manage_users, admin)
 * @param {boolean} requirePrimary - If true, only primary users can see content
 * @param {ReactNode} children - Content to render if permission is granted
 * @param {ReactNode} fallback - Content to render if permission is denied (optional)
 * @param {boolean} showLoading - Whether to show loading state (default: false)
 */
const PermissionGate = ({ 
  permission, 
  requirePrimary = false, 
  children, 
  fallback = null,
  showLoading = false 
}) => {
  const { userOrgData, loading, permissions, isPrimary } = useUserPermissions();

  if (loading && showLoading) {
    return <div>Loading permissions...</div>;
  }

  if (!userOrgData) {
    return fallback;
  }

  // Check if primary role is required
  if (requirePrimary && !isPrimary) {
    return fallback;
  }

  // Check specific permission
  if (permission && !permissions[permission]) {
    return fallback;
  }

  return children;
};

export default PermissionGate;
